import { useEffect, type RefObject } from "react";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface UseDialogKeyboardOptions {
  /** dialog 要素への ref。 focus trap / initial focus の起点。 */
  containerRef: RefObject<HTMLElement | null>;
  /** open=false のとき hook は no-op。 default true。 */
  open?: boolean;
  /** Escape key 押下時の close handler。 */
  onClose: () => void;
}

/**
 * dialog 用 keyboard handler (= Escape close + initial focus + Tab focus trap) を
 * 集約する hook。 SettingsPanel が pattern として確立した focus trap logic を
 * 抽出、 PalimpsestModal / CharacterCodexView / 将来 modal で再利用可能。
 *
 * 動作:
 * - mount (open=true) 時に container 内 first focusable に initial focus
 * - Escape key で onClose 呼出
 * - Tab で container 内 focusable を cycle (= last → first / first → last with Shift)
 *
 * 注意: open=false のときは何もせず early return。 React Hooks ルール準拠で
 * caller は条件付きで hook を呼ばず、 open prop で gating する。
 */
export function useDialogKeyboard({
  containerRef,
  open = true,
  onClose,
}: UseDialogKeyboardOptions): void {
  useEffect(() => {
    if (!open) return;
    const dialog = containerRef.current;
    if (!dialog) return;

    // initial focus: dialog 内 first focusable 要素へ。 children 描画完了を待つため
    // requestAnimationFrame で 1 frame 遅らせる (= portal や conditional render 対策)。
    const rafId = requestAnimationFrame(() => {
      const focusables =
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      focusables[0]?.focus();
    });

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const list = dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (list.length === 0) return;
      const first = list[0]!;
      const last = list[list.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose, containerRef]);
}
